import * as React from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface SearchableComboboxProps {
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  onCreate?: (input: string) => void | Promise<void>;
  placeholder?: string;
  emptyText?: string;
  createLabel?: (input: string) => string;
  disabled?: boolean;
  className?: string;
}

export function SearchableCombobox({
  value,
  options,
  onChange,
  onCreate,
  placeholder = 'Selecciona…',
  emptyText = 'Sin resultados',
  createLabel = (s) => `+ Añadir "${s}"`,
  disabled,
  className,
}: SearchableComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const selected = options.find((o) => o.value === value);
  const normalized = search.trim();
  const exactMatch = options.some(
    (o) => o.label.toLowerCase() === normalized.toLowerCase(),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected?.label ?? value ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-popover z-50"
        align="start"
      >
        <Command shouldFilter>
          <CommandInput
            placeholder="Buscar…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === opt.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
              {onCreate && normalized && !exactMatch && (
                <CommandItem
                  value={`__create__${normalized}`}
                  onSelect={async () => {
                    await onCreate(normalized);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createLabel(normalized)}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
